import { useField } from 'formik';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { useLocalizedTranslation } from '@treams/treams-hooks';
import { DeleteIcon } from '@treams/treams-icons';
import { LocalizedString } from '@treams/treams-network';
import { IconColor, tokens } from '@treams/treams-ui';

import { cellColors } from '../talentReviewColors';
import TalentReviewCell from './TalentReviewTemplateCell';
import TalentReviewLabel from './TalentReviewTemplateLabel';

const Row = styled(Flex)<{ dragging?: boolean }>`
	&:not(:last-child) {
		margin-bottom: ${(props) => (props.dragging ? 14 : 24)}px;
	}
`;

const YLabel = styled(Flex)<{ main?: boolean }>`
	position: relative;
	flex-grow: 0;
	flex-shrink: 0;
	width: 30px;
	align-items: center;

	${(props) => !props.main && `max-height: 120px;`}
`;

const YLabelText = styled(Box)<{ main?: boolean }>`
	${(props) =>
		props.main
			? `
		color: ${tokens.text.secondary};
		font-weight: bold;
	`
			: `
		color: ${props.theme.colors.lightBlueDark};
	`}
`;

const XLabel = styled(Flex)<{ main?: boolean; columns: number; translateX?: number }>`
	flex-grow: 0;
	flex-shrink: 0;
	margin-left: 10px;
	justify-content: center;

	${(props) => props.translateX && `transform: translateX(${props.translateX}px)`};

	${(props) =>
		props.main
			? `
		color: ${tokens.text.secondary};
		font-weight: bold;
		width: ${props.columns * 170 - 10}px;
	`
			: `
		color: ${props.theme.colors.lightBlueDark};
		width: 160px;
	`}
`;

const Placeholder = styled(Flex)`
	border: 2px solid ${tokens.border.subtle};
	border-radius: 6px;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	color: ${tokens.text.secondary};
	cursor: pointer;
	flex-grow: 0;
	flex-shrink: 0;

	&:hover {
		background: ${(props) => props.theme.colors.lightBlueLight};

		&:active {
			background: ${tokens.border.subtle};
		}
	}
`;

const ColumnPlaceholder = styled(Placeholder)`
	width: 40px;
	margin-left: 24px;
`;

const RowPlaceholder = styled(Placeholder)<{ columns: number; delete?: boolean }>`
	height: 40px;
	margin-left: 10px;
	width: ${(props) => props.columns * 170 - 10 + (props.delete ? 50 : 0)}px;
`;

const DeleteContainer = styled(Flex)<{ horizontal?: boolean }>`
	background: ${(props) => rgba(props.theme.colors.red, 0.1)};
	margin-left: 10px;
	border-radius: 4px;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	${(props) =>
		props.horizontal
			? `
		height: 40px;
		width: 160px;
	`
			: `
		height: 120px;
		width: 40px;
	`}

	&:hover {
		background: ${(props) => rgba(props.theme.colors.red, 0.15)};

		&:active {
			background: ${(props) => rgba(props.theme.colors.red, 0.25)};
		}
	}
`;

const handleButtonKeyDown =
	(onClick?: () => void) => (event: { key: string; preventDefault: () => void }) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick?.();
		}
	};

function Delete(props: { horizontal?: boolean; label: string; onClick?: () => void }) {
	return (
		<DeleteContainer
			horizontal={props.horizontal}
			onClick={props.onClick}
			role="button"
			tabIndex={0}
			aria-label={props.label}
			onKeyDown={handleButtonKeyDown(props.onClick)}
		>
			<Box size={16}>
				<IconColor color="red">
					<DeleteIcon />
				</IconColor>
			</Box>
		</DeleteContainer>
	);
}
type TalentReviewCell = {
	color?: number;
	label?: LocalizedString[];
	instruction?: LocalizedString[];
};

export default function TalentReviewGrid(props: { existing?: boolean }) {
	const { t } = useTranslation();
	const { localizedT } = useLocalizedTranslation();

	const theme = useTheme();

	const [{ value: cells }, , { setValue: setCells }] = useField<TalentReviewCell[][]>('cells');
	const [{ value: rows }, , { setValue: setRows }] = useField<LocalizedString[]>('yAxis.ticks');
	const [{ value: columns }, , { setValue: setColumns }] =
		useField<LocalizedString[]>('xAxis.ticks');

	function addColumn() {
		setColumns([
			...columns,
			localizedT('talentReviews.v2.template.placeholders.column', {
				column: columns.length + 1,
			}),
		]);

		setCells(cells.map((row) => [...row, { label: [], instruction: [] }]));
	}

	function addRow() {
		setRows([
			...rows,
			localizedT('talentReviews.v2.template.placeholders.row', {
				row: rows.length + 1,
			}),
		]);

		setCells([...cells, columns.map(() => ({ label: [], instruction: [] }))]);
	}

	function addBoth() {
		setRows([
			...rows,
			localizedT('talentReviews.v2.template.placeholders.row', {
				row: rows.length + 1,
			}),
		]);

		setColumns([
			...columns,
			localizedT('talentReviews.v2.template.placeholders.column', {
				column: columns.length + 1,
			}),
		]);

		setCells([
			...cells.map((row) => [...row, { label: [] }]),
			[...columns, undefined].map(() => ({ label: [] })),
		]);
	}

	function removeColumn(index: number) {
		setColumns(columns.filter((_, i) => i !== index));
		setCells(cells.map((row) => row.filter((_, i) => i !== index)));
	}

	function removeRow(index: number) {
		setRows(rows.filter((_, i) => i !== index));
		setCells(cells.filter((_, i) => i !== index));
	}

	return (
		<Box>
			{!props.existing && rows.length < 5 && (
				<Row mt={24}>
					<YLabel mr={2} />
					<YLabel />
					{rows.length < 5 && (
						<RowPlaceholder
							columns={columns.length}
							delete={rows.length > 1}
							onClick={addRow}
							onKeyDown={handleButtonKeyDown(addRow)}
							role="button"
							tabIndex={0}
							aria-label={t('talentReviews.v2.template.addRow')}
						>
							+
						</RowPlaceholder>
					)}
					{rows.length < 5 && columns.length < 5 && (
						<ColumnPlaceholder
							onClick={addBoth}
							onKeyDown={handleButtonKeyDown(addBoth)}
							role="button"
							tabIndex={0}
							aria-label={t('talentReviews.v2.template.addRowAndColumn')}
						>
							+
						</ColumnPlaceholder>
					)}
				</Row>
			)}
			{columns.length > 1 && !props.existing && (
				<Row mt={24}>
					<YLabel mr={2} />
					<YLabel />
					{columns.map((_, column) => (
						<Delete
							horizontal
							key={column}
							label={t('talentReviews.v2.template.removeColumn')}
							onClick={() => removeColumn(column)}
						/>
					))}
				</Row>
			)}
			<Flex>
				<YLabel mr={2} main>
					<YLabelText main>
						<TalentReviewLabel name="yAxis.label" vertical hex={tokens.text.secondary} />
					</YLabelText>
				</YLabel>
				<Box flexGrow={0} flexShrink={0}>
					{rows
						.map((_, row) => (
							<Row key={row}>
								<YLabel>
									<YLabelText>
										<TalentReviewLabel
											name={`yAxis.ticks[${row}]`}
											vertical
											hex={theme.colors.lightBlueDark}
										/>
									</YLabelText>
								</YLabel>
								{columns.map((_, column) => (
									<TalentReviewCell
										key={column}
										name={`cells[${row}][${column}]`}
										colors={cellColors(row, column, rows.length, columns.length)}
									/>
								))}
								{rows.length > 1 && !props.existing && (
									<Delete
										label={t('talentReviews.v2.template.removeRow')}
										onClick={() => removeRow(row)}
									/>
								)}
							</Row>
						))
						.reverse()}
				</Box>
				{!props.existing && columns.length < 5 && (
					<ColumnPlaceholder
						onClick={addColumn}
						onKeyDown={handleButtonKeyDown(addColumn)}
						role="button"
						tabIndex={0}
						aria-label={t('talentReviews.v2.template.addColumn')}
					>
						+
					</ColumnPlaceholder>
				)}
			</Flex>
			<Box>
				<Row mt={24} mb={0}>
					<YLabel mr={2} />
					<YLabel />
					{columns.map((_, column) => (
						<XLabel key={column} columns={1}>
							<TalentReviewLabel name={`xAxis.ticks[${column}]`} hex={theme.colors.lightBlueDark} />
						</XLabel>
					))}
				</Row>
			</Box>
			<Row mt={2}>
				<YLabel mr={2} />
				<YLabel />
				<XLabel main columns={columns.length}>
					<TalentReviewLabel name="xAxis.label" hex={tokens.text.secondary} />
				</XLabel>
			</Row>
		</Box>
	);
}
